import type {
  ApplyTimelineChangesInput,
  TimelineRepository,
} from '@/features/timeline/application/ports/timeline-repository';
import { createTimelineCommand } from '@/features/timeline/application/timeline-editor-command';
import { TimelineNotFoundError } from '@/features/timeline/application/timeline-errors';
import type { TimelineRecord } from '@/features/timeline/model/timeline-record';
import type { TimelineData } from '@/features/timeline/model/schema';
import { generateScopedId } from '@/features/timeline/application/timeline-id';

const applyCommandsToData = (
  data: TimelineRecord['data'],
  changes: ApplyTimelineChangesInput['changes'],
): TimelineRecord['data'] => {
  let current = structuredClone(data);
  for (const dto of changes) {
    const command = createTimelineCommand(dto);
    const result = command.execute(current);
    if (result) {
      current = result.next;
    }
  }
  return current;
};

const SCHEMA_URL = 'https://timeline-make.app/schema/2.0.json';

const createDefaultTimelineData = (): TimelineData => {
  return {
    pages: [
      {
        id: generateScopedId('page'),
        label: 'Page 1',
        sections: [
          {
            id: generateScopedId('timeline'),
            label: 'Section 1',
            items: [],
          },
        ],
      },
    ],
  };
};

export class LocalFileTimelineRepository implements TimelineRepository {
  private readonly store = new Map<string, TimelineRecord>();

  async findById(id: string): Promise<TimelineRecord | null> {
    return this.store.get(id) ?? null;
  }

  async create(): Promise<TimelineRecord> {
    const id = generateScopedId('timeline');
    const record: TimelineRecord = {
      $schema: SCHEMA_URL,
      id,
      version: '2.0',
      data: createDefaultTimelineData(),
    };
    this.store.set(id, record);
    return record;
  }

  async applyChanges(id: string, input: ApplyTimelineChangesInput): Promise<TimelineRecord> {
    const existing = this.store.get(id);
    if (!existing) {
      throw new TimelineNotFoundError();
    }

    const nextData = applyCommandsToData(existing.data, input.changes);
    const updated: TimelineRecord = {
      ...existing,
      data: nextData,
    };
    this.store.set(id, updated);
    return updated;
  }

  register(record: TimelineRecord): void {
    this.store.set(record.id, record);
  }
}
